import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';


export default function ProjectTimeTracker({ project, updateProject }) {
  const [newEntry, setNewEntry] = useState({ 
      description: '', 
      date: new Date().toISOString().split('T')[0], 
      hours: '', 
      rate: '' 
  });

  const timeEntries = project.timeEntries || [];

  // Calculate totals
  const { totalHours, totalEarnings } = useMemo(() => {
      return timeEntries.reduce((acc, entry) => {
          const hours = parseFloat(entry.hours) || 0;
          const rate = parseFloat(entry.rate) || 0;
          return {
              totalHours: acc.totalHours + hours,
              totalEarnings: acc.totalEarnings + (hours * rate)
          };
      }, { totalHours: 0, totalEarnings: 0 });
  }, [timeEntries]);

  const handleAddEntry = (e) => {
      e.preventDefault();
      if (!newEntry.description || !newEntry.hours) return;

      const updatedEntries = [
          ...timeEntries, 
          { 
              ...newEntry, 
              id: Date.now().toString(),
              hours: parseFloat(newEntry.hours),
              rate: parseFloat(newEntry.rate) || 0
          }
      ].sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort newest first

      updateProject(project.id, { timeEntries: updatedEntries });
      
      // Reset form but keep the rate for convenience
      setNewEntry(prev => ({ 
          description: '', 
          date: new Date().toISOString().split('T')[0], 
          hours: '', 
          rate: prev.rate 
      }));
  };

  const handleRemoveEntry = (id) => {
      if (window.confirm("Are you sure you want to delete this time entry?")) {
          const updatedEntries = timeEntries.filter(entry => entry.id !== id);
          updateProject(project.id, { timeEntries: updatedEntries });
      }
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
        {/* Header & Stats */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
                <h2 className="text-2xl font-bold font-serif text-gray-900 flex items-center gap-2">
                    <PlannerIcons.Clock /> Time Tracker
                </h2>
                <p className="text-sm text-gray-500 mt-1">Log your billable hours and track planning time.</p>
            </div>
            
            <div className="flex gap-4 w-full sm:w-auto">
                <div className="bg-gray-50 px-4 py-3 rounded-xl border border-gray-100 flex-1 sm:flex-none">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Hours</p>
                    <p className="text-2xl font-black text-gray-900">{totalHours.toFixed(1)}h</p>
                </div>
                <div className="bg-emerald-50 px-4 py-3 rounded-xl border border-emerald-100 flex-1 sm:flex-none">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1">Billable</p>
                    <p className="text-2xl font-black text-emerald-700">${totalEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
            </div>
        </div>

        {/* Add Entry Form */}
        <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Log Time</h3>
            <form onSubmit={handleAddEntry} className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="w-full sm:flex-1">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Task Description</label>
                    <input 
                        type="text" 
                        required
                        value={newEntry.description} 
                        onChange={e => setNewEntry({...newEntry, description: e.target.value})} 
                        placeholder="e.g. Venue Tour, Client Call..." 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="w-full sm:w-40">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Date</label>
                    <input 
                        type="date" 
                        required
                        value={newEntry.date} 
                        onChange={e => setNewEntry({...newEntry, date: e.target.value})} 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white text-gray-700"
                    />
                </div>
                <div className="w-full sm:w-28 border-l border-gray-200 pl-3">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Hours</label>
                    <input 
                        type="number" 
                        required
                        step="0.25"
                        min="0"
                        value={newEntry.hours} 
                        onChange={e => setNewEntry({...newEntry, hours: e.target.value})} 
                        placeholder="0.0" 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="w-full sm:w-28">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Rate/Hr ($)</label>
                    <input 
                        type="number" 
                        step="1"
                        min="0"
                        value={newEntry.rate} 
                        onChange={e => setNewEntry({...newEntry, rate: e.target.value})} 
                        placeholder="0" 
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <button 
                    type="submit" 
                    disabled={!newEntry.description || !newEntry.hours}
                    className="w-full sm:w-auto bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap h-[42px]"
                >
                    <PlannerIcons.Plus /> Log
                </button>
            </form>
        </div>

        {/* Time Log Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
                            <th className="p-4 font-bold">Date</th>
                            <th className="p-4 font-bold w-1/2">Description</th>
                            <th className="p-4 font-bold text-right">Hours</th>
                            <th className="p-4 font-bold text-right">Rate</th>
                            <th className="p-4 font-bold text-right">Total</th>
                            <th className="p-4 font-bold text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {timeEntries.map((entry) => {
                            const entryTotal = (parseFloat(entry.hours) || 0) * (parseFloat(entry.rate) || 0);
                            return (
                                <tr key={entry.id} className="hover:bg-gray-50/50 transition group">
                                    <td className="p-4 text-sm font-medium text-gray-600 whitespace-nowrap">
                                        {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </td>
                                    <td className="p-4 text-sm font-bold text-gray-900">
                                        {entry.description}
                                    </td>
                                    <td className="p-4 text-sm font-medium text-gray-700 text-right">
                                        <span className="bg-gray-100 px-2 py-1 rounded">{entry.hours}h</span>
                                    </td>
                                    <td className="p-4 text-sm font-medium text-gray-500 text-right">
                                        ${parseFloat(entry.rate || 0).toFixed(2)}/h
                                    </td>
                                    <td className="p-4 text-sm font-bold text-emerald-600 text-right">
                                        ${entryTotal.toFixed(2)}
                                    </td>
                                    <td className="p-4 text-center">
                                        <button
                                            onClick={() => handleRemoveEntry(entry.id)}
                                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                                            title="Delete Entry"
                                        >
                                            <PlannerIcons.Trash />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {timeEntries.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-12 text-center text-gray-400">
                                    <div className="text-3xl mb-2 opacity-50">⏱️</div>
                                    <p className="text-sm font-medium">No time logged yet.</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );
}